import { Navigate, useLocation, useSearchParams } from 'react-router';

import { ROUTES, useRouteParams } from '../../constants/routes';
import { parseExpandParam } from '../../pages/ProtocolLibraryGrid/parseExpandParam';
import { DESTINATION_VIEW_STATE } from '../../utils/workspaceNavState';
import { mapLegacyWorkspaceLocation } from './legacyWorkspaceRedirects';

/**
 * The URL shapes Bower has renamed, each preserved as a redirect so links
 * already in the wild — a shared note, a bookmark, an email from last year —
 * still land on the right record.
 *
 * Extracted from `App.tsx` when that file reached the 1000-line guard: they are
 * routing concerns, they belong beside the other routing components, and
 * nothing in the app shell reads them apart from the route table.
 */

/** Legacy `/samples/:id` → `/collections/:id` (samples are now collections). */
export function SampleRedirect() {
  const { sampleId } = useRouteParams('samplesLegacy');
  return <Navigate to={ROUTES.collection(sampleId)} replace />;
}

/** Legacy `/project/:id` → `/collections/:id`. */
export function ProjectRedirect() {
  const { projectId } = useRouteParams('projectLegacy');
  return <Navigate to={ROUTES.collection(projectId)} replace />;
}

/** Legacy `/projects/:id` → `/collections/:id`. */
export function ProjectsLegacyRedirect() {
  const { projectId } = useRouteParams('projectsLegacy');
  return <Navigate to={ROUTES.collection(projectId)} replace />;
}

/** Legacy `/m/notes/:noteId` → the unified `/notes/:noteId`. */
export function MobileNoteRedirect() {
  const { noteId } = useRouteParams('mobileNoteLegacy');
  return <Navigate to={ROUTES.note(noteId)} replace />;
}

/** Legacy `/m/attachments/:attachmentId` → the unified `/attachments/:attachmentId`. */
export function MobileAttachmentRedirect() {
  const { attachmentId } = useRouteParams('mobileAttachmentLegacy');
  return <Navigate to={ROUTES.attachment(attachmentId)} replace />;
}

/**
 * Legacy workspace list pages (`/artifacts`, `/equipment`) to the workspace
 * files list. A path the mapper does not know falls back to the collections
 * list rather than looping back to itself. The list's filter is the page, so
 * it does not become the remembered choice (`DESTINATION_VIEW_STATE`).
 */
export function LegacyWorkspaceListRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={mapLegacyWorkspaceLocation(location) ?? ROUTES.collections}
      state={DESTINATION_VIEW_STATE}
      replace
    />
  );
}

/**
 * Legacy `/capture-stream` (the Protocol runs feed, retired once runs moved
 * inside Protocols) → the Protocols list, one valid `?protocol=` carried over
 * as its Protocol filter. Not the protocol page: a note-backed protocol redirects from
 * there back to its note. Anything else (several ids, a malformed one, none)
 * lands on the plain list.
 */
export function CaptureStreamRedirect() {
  const [params] = useSearchParams();
  const protocol = parseExpandParam(params.get('protocol'));
  return (
    <Navigate
      to={protocol ? `${ROUTES.protocols}?protocol=${protocol}` : ROUTES.protocols}
      replace
    />
  );
}
